<template>
  <div class="page-shell">
    <section class="page-panel" v-loading="loading">
      <div class="page-header">
        <div>
          <p class="eyebrow">管理员配置中心</p>
          <h1>模型与密钥配置</h1>
          <p class="page-subtitle">统一维护运行时默认接入、可用模型池、已发布目录与交付存储配置。</p>
        </div>
        <div class="header-actions">
          <el-button @click="router.push('/admin')">返回管理后台</el-button>
          <el-button type="primary" :loading="saving" @click="handleSave">保存配置</el-button>
        </div>
      </div>

      <div class="status-grid">
        <article class="status-card">
          <span>文本接入方式</span>
          <strong>{{ llmBackendLabel }}</strong>
        </article>
        <article class="status-card status-card--soft">
          <span>文本供应商</span>
          <strong>{{ form.llm_provider || '未设置' }}</strong>
        </article>
        <article class="status-card status-card--accent">
          <span>就绪集成</span>
          <strong>{{ readyIntegrationCount }} / {{ integrationStatus.length || 6 }}</strong>
        </article>
      </div>

      <section class="card-block status-board">
        <div class="block-header">
          <div>
            <h2>当前接入状态</h2>
            <p class="block-subtitle">区分“参数已保存”和“运行时真的可用”，先判断系统能不能跑，再决定是否继续接新厂商。</p>
          </div>
          <el-tag :type="readyIntegrationCount > 0 ? 'success' : 'warning'">{{ readyIntegrationCount }} 条已可用</el-tag>
        </div>

        <div class="integration-grid">
          <article v-for="item in integrationStatus" :key="item.key" class="integration-card">
            <div class="integration-card__head">
              <div>
                <h3>{{ item.label }}</h3>
                <p>{{ item.provider || '未设置供应商' }}</p>
              </div>
              <el-tag :type="integrationTagType(item)">{{ integrationTagLabel(item) }}</el-tag>
            </div>
            <div class="integration-meta">
              <span v-if="item.mode">模式：{{ item.mode }}</span>
              <span>实现：{{ item.implemented ? '已实现' : '待补接' }}</span>
              <span>配置：{{ item.configured ? '完整' : '不完整' }}</span>
            </div>
            <p class="integration-detail">{{ item.detail }}</p>
          </article>
        </div>
      </section>

      <el-form label-position="top" class="settings-form">
        <div class="settings-grid">
          <section class="card-block">
            <div class="block-header">
              <div>
                <h2>文本生成主配置</h2>
                <p class="block-subtitle">主链路默认走直连模型；只有确实需要工作流编排时，才启用 Dify 作为补充入口。</p>
              </div>
              <el-tag :type="providerTagType">{{ providerTagLabel }}</el-tag>
            </div>

            <div class="form-grid">
              <el-form-item label="文本接入模式">
                <el-select v-model="form.llm_backend">
                  <el-option label="直连模型" value="openclaw" />
                  <el-option label="工作流编排（Dify）" value="dify" />
                  <el-option label="混合回退" value="hybrid" />
                </el-select>
              </el-form-item>
              <el-form-item label="LLM 供应商">
                <el-select v-model="form.llm_provider">
                  <el-option label="通义千问 / DashScope" value="ali_tongyi" />
                  <el-option label="Azure OpenAI / GPT" value="azure" />
                  <el-option label="Gemini" value="gemini" />
                  <el-option label="OpenAI" value="openai" />
                </el-select>
              </el-form-item>
              <el-form-item label="API Base URL">
                <el-input v-model="form.llm_api_base" placeholder="通义可用 DashScope，Azure GPT 填 Azure/OpenAI 兼容网关，OpenAI 用 https://api.openai.com/v1，Gemini 用 Google v1beta" />
              </el-form-item>
              <el-form-item label="对话模型">
                <el-select v-if="isTongyiProvider" v-model="form.llm_model_chat" filterable>
                  <el-option v-for="item in tongyiFreeModelOptions" :key="`chat-${item}`" :label="item" :value="item" />
                </el-select>
                <el-input v-else v-model="form.llm_model_chat" placeholder="例如：gpt-4o-mini / gemini-2.0-flash" />
              </el-form-item>
              <el-form-item label="分析模型">
                <el-select v-if="isTongyiProvider" v-model="form.llm_model_analysis" filterable>
                  <el-option v-for="item in tongyiFreeModelOptions" :key="`analysis-${item}`" :label="item" :value="item" />
                </el-select>
                <el-input v-else v-model="form.llm_model_analysis" placeholder="例如：gpt-4.1 / gemini-2.5-pro" />
              </el-form-item>
              <el-form-item label="审核模型">
                <el-select v-if="isTongyiProvider" v-model="form.llm_model_review" filterable>
                  <el-option v-for="item in tongyiFreeModelOptions" :key="`review-${item}`" :label="item" :value="item" />
                </el-select>
                <el-input v-else v-model="form.llm_model_review" placeholder="例如：gpt-4.1-mini / gemini-2.0-flash" />
              </el-form-item>
            </div>

            <div v-if="isTongyiProvider" class="llm-guardrail-note">
              通义千问已启用严格白名单控制，只允许使用免费额度模型。非白名单模型无法保存，运行时也会自动回退到免费模型。
            </div>

            <el-form-item label="LLM API Key">
              <el-input v-model="form.llm_api_key" type="password" show-password placeholder="用于直连通义、Azure GPT、OpenAI 或 Gemini 接口" />
            </el-form-item>
          </section>

          <section class="card-block card-block--subtle">
            <div class="block-header">
              <div>
                <h2>工作流编排（可选）</h2>
                <p class="block-subtitle">Dify 还存在真实运行时入口，但只在你明确选择工作流编排或混合回退时才需要配置。</p>
              </div>
              <el-tag type="warning">按需启用</el-tag>
            </div>

            <el-form-item label="Dify API Base URL">
              <el-input v-model="form.dify_api_base" placeholder="例如：https://api.dify.ai/v1" />
            </el-form-item>

            <el-form-item label="Dify API Key">
              <el-input v-model="form.dify_api_key" type="password" show-password placeholder="Dify 工作区 API Key" />
            </el-form-item>

            <div class="form-grid">
              <el-form-item label="需求分析 Workflow ID">
                <el-input v-model="form.dify_workflow_analysis" placeholder="demand-analysis workflow id" />
              </el-form-item>
              <el-form-item label="脚本生成 Workflow ID">
                <el-input v-model="form.dify_workflow_script" placeholder="script workflow id" />
              </el-form-item>
            </div>

            <el-form-item label="审核 Workflow ID">
              <el-input v-model="form.dify_workflow_review" placeholder="review workflow id" />
            </el-form-item>
          </section>

          <section class="card-block">
            <div class="block-header">
              <div>
                <h2>生成能力配置</h2>
                <p class="block-subtitle">上半部分决定系统运行时默认接哪家厂商、用哪个模型；下半部分把已接入模型发布到前台或系统目录。</p>
              </div>
              <el-tag type="info">运行时 + 用户目录</el-tag>
            </div>

            <div class="section-stack">
              <div>
                <h3 class="section-title">运行时默认接入</h3>
                <p class="section-note">这里配置后端实际调用的多模态厂商和默认模型，优先保证稳定可用。切换供应商时，各家的参数会分开保留，不会互相覆盖。</p>
              </div>

            <div v-if="isTongyiImageProvider || isTongyiVideoProvider" class="queue-board">
              <article v-if="isTongyiImageProvider" class="queue-card">
                <div class="queue-card__header">
                  <h3>图片模型队列</h3>
                  <span>{{ tongyiImageQueuePreview.length ? `${tongyiImageQueuePreview.length} 个候选` : '仅默认模型' }}</span>
                </div>
                <div v-if="tongyiImageQueuePreview.length" class="queue-list">
                  <div v-for="item in tongyiImageQueuePreview" :key="`image-queue-${item.value}-${item.priority}`" class="queue-list__item">
                    <strong>P{{ item.priority }}</strong>
                    <div>
                      <span>{{ item.value }}</span>
                      <small>{{ item.capability }} · 到期 {{ item.expiresAt }}</small>
                    </div>
                  </div>
                </div>
                <p v-else class="queue-card__empty">未设置优先队列时，将直接使用当前默认图片模型。</p>
              </article>

              <article v-if="isTongyiVideoProvider" class="queue-card queue-card--video">
                <div class="queue-card__header">
                  <h3>视频模型队列</h3>
                  <span>{{ tongyiVideoQueuePreview.length ? `${tongyiVideoQueuePreview.length} 个候选` : '仅默认模型' }}</span>
                </div>
                <div v-if="tongyiVideoQueuePreview.length" class="queue-list">
                  <div v-for="item in tongyiVideoQueuePreview" :key="`video-queue-${item.value}-${item.priority}`" class="queue-list__item">
                    <strong>P{{ item.priority }}</strong>
                    <div>
                      <span>{{ item.value }}</span>
                      <small>{{ item.capability }} · 到期 {{ item.expiresAt }}</small>
                    </div>
                  </div>
                </div>
                <p v-else class="queue-card__empty">未设置优先队列时，将直接使用当前默认视频模型。</p>
              </article>
            </div>

            <div class="quad-grid">
              <div class="nested-panel">
                <h3>图像生成</h3>
                <el-form-item label="供应商">
                  <el-select v-model="form.image_gen_provider">
                    <el-option label="火山引擎 / 方舟" value="volcengine" />
                    <el-option label="fal" value="fal" />
                    <el-option label="即梦" value="jimeng" />
                    <el-option label="可灵" value="kling" />
                    <el-option label="Midjourney" value="midjourney" />
                    <el-option label="阿里通义" value="ali_tongyi" />
                  </el-select>
                </el-form-item>
                <el-form-item label="API Base URL">
                  <el-input v-model="form.image_gen_api_base" :placeholder="isTongyiImageProvider ? '留空可复用通义官方图片端点与文本 Key' : (isVolcImageProvider ? '留空将自动使用火山方舟图片生成端点' : (isFalImageProvider ? '填写 fal 或内部代理的图片生成端点' : '必填，填写图片生成端点或内部代理网关地址'))" />
                </el-form-item>
                <el-form-item label="默认模型">
                  <el-select v-if="isTongyiImageProvider || isVolcImageProvider || isFalImageProvider" v-model="form.image_gen_model" filterable allow-create default-first-option>
                    <el-option v-for="item in activeImageModelOptions" :key="item.value" :label="item.label" :value="item.value" />
                  </el-select>
                  <el-input v-else v-model="form.image_gen_model" placeholder="例如：sdxl / jimeng-3.0" />
                </el-form-item>
                <el-form-item v-if="isTongyiImageProvider" label="优先模型队列">
                  <el-select v-model="form.image_gen_model_priority" multiple filterable allow-create default-first-option collapse-tags collapse-tags-tooltip placeholder="按到期优先级排序，第一项会优先使用">
                    <el-option v-for="item in tongyiImageModelOptions" :key="item.value" :label="item.label" :value="item.value" />
                  </el-select>
                </el-form-item>
                <el-form-item label="API Key">
                  <el-input v-model="form.image_gen_api_key" type="password" show-password :placeholder="isTongyiImageProvider || isVolcImageProvider ? '留空可复用上方文本链路的 API Key' : ''" />
                </el-form-item>
                <el-form-item label="API Secret">
                  <el-input v-model="form.image_gen_api_secret" type="password" show-password />
                </el-form-item>
              </div>

              <div class="nested-panel">
                <h3>TTS</h3>
                <el-form-item label="供应商">
                  <el-select v-model="form.tts_provider">
                    <el-option label="阿里云智能语音" value="aliyun" />
                    <el-option label="火山引擎语音" value="volcengine" />
                    <el-option label="Azure" value="azure" />
                    <el-option label="ElevenLabs" value="elevenlabs" />
                    <el-option label="讯飞" value="xunfei" />
                    <el-option label="魔音" value="moyin" />
                  </el-select>
                </el-form-item>
                <el-form-item label="API Base URL">
                  <el-input v-model="form.tts_api_base" :placeholder="isAliyunTtsProvider ? '留空将使用阿里云 NLS 官方网关' : (isVolcTtsProvider ? '留空将使用火山引擎 OpenSpeech 官方网关' : '选填，按厂商网关填写')" />
                </el-form-item>
                <el-form-item :label="isAliyunTtsProvider ? 'AccessKey ID / 临时 Token' : 'API Key / Token'">
                  <el-input v-model="form.tts_api_key" type="password" show-password :placeholder="isAliyunTtsProvider ? '长期调用填写阿里云 AccessKey ID；仅短测可填写 24 小时临时 Token' : (isVolcTtsProvider ? '可填写 Access Token，或控制台快捷接入里的 UUID API Key' : '')" />
                </el-form-item>
                <el-form-item :label="isAliyunTtsProvider ? 'AccessKey Secret（长期调用）' : 'API Secret / Secret'">
                  <el-input v-model="form.tts_api_secret" type="password" show-password :placeholder="isAliyunTtsProvider ? '长期调用必填，用于服务端自动换取 Token' : (isVolcTtsProvider ? '快捷 API 模式通常留空；仅按特定厂商鉴权要求填写' : '按厂商要求填写')" />
                </el-form-item>
                <el-form-item :label="isAliyunTtsProvider ? 'App Key' : (isVolcTtsProvider ? 'App ID' : 'App Key / App ID')">
                  <el-input v-model="form.tts_app_key" :placeholder="isAliyunTtsProvider ? '填写阿里云 AppKey' : (isVolcTtsProvider ? 'Bearer 模式填写火山 AppID；快捷 API 模式可留空' : '按厂商要求填写')" />
                </el-form-item>
                <el-form-item :label="isAzureTtsProvider ? '区域' : (isVolcTtsProvider ? '集群 / 区域' : '区域 / 机房')">
                  <el-input v-model="form.tts_region" :placeholder="isAzureTtsProvider ? '例如：eastasia' : (isVolcTtsProvider ? '例如：volcano_tts' : '例如：cn-shanghai')" />
                </el-form-item>
                <el-form-item label="默认音色">
                  <el-input v-model="form.tts_voice_id" :placeholder="isAliyunTtsProvider ? '例如：xiaoyun' : (isVolcTtsProvider ? '例如：BV001_streaming' : '例如：zh-CN-XiaoxiaoNeural')" />
                </el-form-item>
                <el-form-item :label="isVolcTtsProvider ? '模型 / Cluster' : '默认模型'">
                  <el-input v-model="form.tts_model" :placeholder="isVolcTtsProvider ? '例如：volcano_tts' : '例如：multilingual-v2'" />
                </el-form-item>
              </div>

              <div class="nested-panel">
                <h3>视频生成</h3>
                <el-form-item label="视频供应商">
                  <el-select v-model="form.video_gen_provider">
                    <el-option label="火山引擎 / 方舟" value="volcengine" />
                    <el-option label="即梦（字节）" value="jimeng" />
                    <el-option label="可灵" value="kling" />
                    <el-option label="Runway" value="runway" />
                    <el-option label="阿里通义 / Wan 视频" value="ali_tongyi" />
                  </el-select>
                </el-form-item>
                <el-form-item label="API Base URL">
                  <el-input v-model="form.video_gen_api_base" :placeholder="isTongyiVideoProvider ? '留空可复用通义官方视频端点与文本 Key' : (isVolcVideoProvider ? '留空将自动使用火山方舟视频生成端点' : '必填，填写视频生成端点或内部代理网关地址')" />
                </el-form-item>
                <el-form-item label="默认模型">
                  <el-select v-if="isTongyiVideoProvider || isVolcVideoProvider" v-model="form.video_gen_model" filterable allow-create default-first-option>
                    <el-option v-for="item in activeVideoModelOptions" :key="item.value" :label="item.label" :value="item.value" />
                  </el-select>
                  <el-input v-else v-model="form.video_gen_model" placeholder="例如：gen-3-alpha / pika-2.2" />
                </el-form-item>
                <el-form-item v-if="isTongyiVideoProvider" label="优先模型队列">
                  <el-select v-model="form.video_gen_model_priority" multiple filterable allow-create default-first-option collapse-tags collapse-tags-tooltip placeholder="按优先级排序，第一项会优先用于视频生成">
                    <el-option v-for="item in tongyiVideoModelOptions" :key="item.value" :label="item.label" :value="item.value" />
                  </el-select>
                </el-form-item>
                <el-form-item label="视频 API Key">
                  <el-input v-model="form.video_gen_api_key" type="password" show-password :placeholder="isTongyiVideoProvider || isVolcVideoProvider ? '留空可复用上方文本链路的 API Key' : ''" />
                </el-form-item>
                <el-form-item label="视频 API Secret">
                  <el-input v-model="form.video_gen_api_secret" type="password" show-password />
                </el-form-item>
                <div v-if="isTongyiVideoProvider" class="media-config-note">
                  wan2.6-i2v-flash 和 wan2.6-r2v-flash 已归入视频生成配置，不再出现在图片模型队列中。
                </div>
              </div>

              <div class="nested-panel">
                <h3>内容审核</h3>
                <el-form-item label="审核供应商">
                  <el-select v-model="form.compliance_provider">
                    <el-option label="阿里云" value="aliyun" />
                    <el-option label="百度" value="baidu" />
                    <el-option label="腾讯" value="tencent" />
                  </el-select>
                </el-form-item>
                <el-form-item label="API Base URL">
                  <el-input v-model="form.compliance_api_base" placeholder="例如：https://green.aliyuncs.com" />
                </el-form-item>
                <el-form-item label="审核模型 / 场景">
                  <el-input v-model="form.compliance_model" placeholder="例如：spam,porn,politics" />
                </el-form-item>
                <el-form-item label="审核 API Key">
                  <el-input v-model="form.compliance_api_key" type="password" show-password />
                </el-form-item>
                <el-form-item label="审核 Secret">
                  <el-input v-model="form.compliance_secret" type="password" show-password />
                </el-form-item>
              </div>
            </div>

              <div class="section-divider"></div>

              <ModelCapabilityWorkbench
                v-model="modelCatalogs"
                :seed-catalog="runtimeSeedCatalogs"
                :text-provider="form.llm_provider"
                :image-provider="form.image_gen_provider"
                :video-provider="form.video_gen_provider"
                :compliance-provider="form.compliance_provider"
              />
            </div>
          </section>

          <section class="card-block">
            <div class="block-header">
              <h2>OSS 存储</h2>
              <el-tag type="success">交付资源</el-tag>
            </div>

            <div class="form-grid">
              <el-form-item label="OSS Endpoint">
                <el-input v-model="form.oss_endpoint" placeholder="例如：oss-cn-hangzhou.aliyuncs.com" />
              </el-form-item>
              <el-form-item label="Bucket 名称">
                <el-input v-model="form.oss_bucket_name" placeholder="例如：videogenerate-auto" />
              </el-form-item>
              <el-form-item label="Access Key ID">
                <el-input v-model="form.oss_access_key_id" type="password" show-password />
              </el-form-item>
              <el-form-item label="Access Key Secret">
                <el-input v-model="form.oss_access_key_secret" type="password" show-password />
              </el-form-item>
            </div>
          </section>
        </div>
      </el-form>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'

import {
  configApi,
  type APIKeyConfig,
  type APIKeyUpdate,
  type ComplianceProviderConfig,
  type IntegrationStatusItem,
  type LLMProviderConfig,
  type MediaProviderConfig,
  type ModelCatalogConfig,
  type ModelCatalogItem,
  type TTSProviderConfig,
} from '@/api/config'
import { useUserStore } from '@/stores/user'
import ModelCapabilityWorkbench from '@/views/customer/settings/ModelCapabilityWorkbench.vue'
import {
  azureDefaultModels,
  buildModelQueuePreview,
  clonePriorityModels,
  DASHSCOPE_VIDEO_API_BASE,
  falImageModelOptions,
  FAL_DEFAULT_IMAGE_MODEL,
  normalizeComplianceProviderConfig,
  normalizeImageProviderConfig,
  normalizeLLMProviderConfig,
  normalizeTtsProviderConfig,
  normalizeVideoProviderConfig,
  resolveDashscopeImageApiBase,
  sanitizeTongyiModel,
  tongyiDefaultModels,
  tongyiFreeModelOptions,
  tongyiImageModelOptions,
  tongyiVideoModelOptions,
  TONGYI_DEFAULT_IMAGE_MODEL,
  TONGYI_DEFAULT_VIDEO_MODEL,
  volcengineImageModelOptions,
  volcengineVideoModelOptions,
  VOLCENGINE_ARK_IMAGE_API_BASE,
  VOLCENGINE_ARK_VIDEO_API_BASE,
  VOLCENGINE_DEFAULT_IMAGE_MODEL,
  VOLCENGINE_DEFAULT_VIDEO_MODEL,
} from '@/views/customer/settings/runtimeProviderPools'

type ModelCatalogField = keyof ModelCatalogConfig

type SettingsForm = Required<Omit<APIKeyConfig, 'llm_provider_pool' | 'image_provider_pool' | 'tts_provider_pool' | 'video_provider_pool' | 'compliance_provider_pool'>>
type ProviderScopedMediaConfig = MediaProviderConfig

function createCatalogItem(item: Partial<ModelCatalogItem> & Pick<ModelCatalogItem, 'id' | 'label' | 'model' | 'provider'>): ModelCatalogItem {
  return {
    description: '',
    capability: '',
    enabled: true,
    is_default: false,
    ...item,
    operations: [...(item.operations || [])],
  }
}

function buildSeedItemId(kind: 'text' | 'image' | 'video' | 'compliance', provider: string, model: string) {
  return `${kind}-${provider}-${model}`.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

function buildKnownMediaCatalogSeedItems(
  kind: 'image' | 'video',
  provider: string,
  options: Array<{ label: string; value: string; expiresAt: string; capability: string }>,
) {
  return options.map((item) => createCatalogItem({
    id: buildSeedItemId(kind, provider, item.value),
    label: item.label.split('·')[0].trim() || item.value,
    model: item.value,
    provider,
    description: item.expiresAt,
    capability: item.capability,
    enabled: true,
    is_default: false,
  }))
}

function normalizeCatalogField(field: ModelCatalogField, items: ModelCatalogItem[]): ModelCatalogItem[] {
  const normalized = items.map((item) => ({
    ...item,
    operations: field === 'text_models' ? [...(item.operations || [])] : [],
  }))
  const hasEnabledDefault = normalized.some((item) => item.enabled && item.is_default)
  let defaultAssigned = false
  return normalized.map((item) => {
    if (!item.enabled) {
      return { ...item, is_default: false }
    }
    if (!defaultAssigned && item.is_default) {
      defaultAssigned = true
      return item
    }
    if (!defaultAssigned && !hasEnabledDefault) {
      defaultAssigned = true
      return { ...item, is_default: true }
    }
    return { ...item, is_default: false }
  })
}

function mergeCatalogFieldWithSeeds(field: ModelCatalogField, existingItems: ModelCatalogItem[], seedItems: ModelCatalogItem[]) {
  const nextItems = existingItems.map((item) => ({
    ...item,
    operations: field === 'text_models' ? [...(item.operations || [])] : [],
  }))

  seedItems.forEach((seed) => {
    if (!String(seed.model || '').trim()) {
      return
    }
    const matchIndex = nextItems.findIndex((item) => (
      item.id === seed.id || (
        String(item.provider || '').trim() === String(seed.provider || '').trim() &&
        String(item.model || '').trim() === String(seed.model || '').trim()
      )
    ))
    if (matchIndex >= 0) {
      const current = nextItems[matchIndex]
      nextItems[matchIndex] = {
        ...current,
        ...seed,
        enabled: current.enabled,
        is_default: current.is_default,
        operations: field === 'text_models'
          ? (current.operations?.length ? [...current.operations] : [...(seed.operations || [])])
          : [],
      }
      return
    }
    nextItems.push({
      ...seed,
      operations: field === 'text_models' ? [...(seed.operations || [])] : [],
    })
  })

  return normalizeCatalogField(field, nextItems)
}

const router = useRouter()
const userStore = useUserStore()

const loading = ref(false)
const saving = ref(false)
const integrationStatus = ref<IntegrationStatusItem[]>([])
const modelCatalogs = ref<ModelCatalogConfig>({
  text_models: [],
  image_models: [],
  video_models: [],
  compliance_models: [],
})
const isTongyiProvider = computed(() => form.llm_provider === 'ali_tongyi')
const isTongyiImageProvider = computed(() => form.image_gen_provider === 'ali_tongyi')
const isTongyiVideoProvider = computed(() => form.video_gen_provider === 'ali_tongyi')
const isVolcImageProvider = computed(() => form.image_gen_provider === 'volcengine')
const isVolcVideoProvider = computed(() => form.video_gen_provider === 'volcengine')
const isFalImageProvider = computed(() => form.image_gen_provider === 'fal')
const isAzureTtsProvider = computed(() => form.tts_provider === 'azure')
const isAliyunTtsProvider = computed(() => form.tts_provider === 'aliyun')
const isVolcTtsProvider = computed(() => form.tts_provider === 'volcengine')

const form = reactive<SettingsForm>({
  llm_backend: 'openclaw',
  llm_provider: 'ali_tongyi',
  llm_api_key: '',
  llm_api_base: 'https://dashscope.aliyuncs.com/compatible-mode/v1',
  llm_model_chat: tongyiDefaultModels.chat,
  llm_model_analysis: tongyiDefaultModels.analysis,
  llm_model_review: tongyiDefaultModels.review,
  dify_api_base: 'https://api.dify.ai/v1',
  dify_api_key: '',
  dify_workflow_analysis: '',
  dify_workflow_script: '',
  dify_workflow_review: '',
  image_gen_provider: 'volcengine',
  image_gen_api_base: VOLCENGINE_ARK_IMAGE_API_BASE,
  image_gen_api_key: '',
  image_gen_api_secret: '',
  image_gen_model: VOLCENGINE_DEFAULT_IMAGE_MODEL,
  image_gen_model_priority: [],
  tts_provider: 'aliyun',
  tts_api_base: '',
  tts_api_key: '',
  tts_api_secret: '',
  tts_app_key: '',
  tts_region: 'cn-shanghai',
  tts_voice_id: 'xiaoyun',
  tts_model: 'default',
  video_gen_provider: 'volcengine',
  video_gen_api_base: VOLCENGINE_ARK_VIDEO_API_BASE,
  video_gen_api_key: '',
  video_gen_api_secret: '',
  video_gen_model: VOLCENGINE_DEFAULT_VIDEO_MODEL,
  video_gen_model_priority: [],
  compliance_provider: 'aliyun',
  compliance_api_base: 'https://green.aliyuncs.com',
  compliance_api_key: '',
  compliance_secret: '',
  compliance_model: 'default',
  oss_endpoint: 'oss-cn-hangzhou.aliyuncs.com',
  oss_bucket_name: 'videogenerate-auto',
  oss_access_key_id: '',
  oss_access_key_secret: '',
})

const providerTagType = computed(() => (form.llm_backend === 'dify' ? 'warning' : form.llm_backend === 'hybrid' ? 'success' : 'primary'))
const providerTagLabel = computed(() => {
  if (form.llm_backend === 'dify') return '工作流编排'
  if (form.llm_backend === 'hybrid') return '混合回退'
  return '直连模型'
})

const llmBackendLabel = computed(() => {
  if (form.llm_backend === 'dify') return '工作流编排'
  if (form.llm_backend === 'hybrid') return '混合回退'
  return '直连模型'
})

const readyIntegrationCount = computed(() => integrationStatus.value.filter((item) => item.ready).length)
const tongyiImageQueuePreview = computed(() => buildModelQueuePreview(tongyiImageModelOptions, form.image_gen_model_priority, form.image_gen_model))
const tongyiVideoQueuePreview = computed(() => buildModelQueuePreview(tongyiVideoModelOptions, form.video_gen_model_priority, form.video_gen_model))
const knownImageCatalogSeeds = computed(() => ([
  ...buildKnownMediaCatalogSeedItems('image', 'ali_tongyi', tongyiImageModelOptions),
  ...buildKnownMediaCatalogSeedItems('image', 'volcengine', volcengineImageModelOptions),
]))
const knownVideoCatalogSeeds = computed(() => ([
  ...buildKnownMediaCatalogSeedItems('video', 'ali_tongyi', tongyiVideoModelOptions),
  ...buildKnownMediaCatalogSeedItems('video', 'volcengine', volcengineVideoModelOptions),
]))
const activeImageModelOptions = computed(() => {
  if (isVolcImageProvider.value) {
    return volcengineImageModelOptions
  }
  if (isFalImageProvider.value) {
    return falImageModelOptions
  }
  return tongyiImageModelOptions
})
const activeVideoModelOptions = computed(() => isVolcVideoProvider.value ? volcengineVideoModelOptions : tongyiVideoModelOptions)
const runtimeSeedCatalogs = computed<ModelCatalogConfig>(() => ({
  text_models: [
    createCatalogItem({
      id: 'text-chat-default',
      label: '默认对话模型',
      model: String(form.llm_model_chat || '').trim(),
      provider: form.llm_provider,
      description: '用于内容生成与内容植入。',
      capability: '文本生成',
      enabled: true,
      is_default: true,
      operations: ['content_seed_generate', 'content_embed', 'workflow_script_generate'],
    }),
    createCatalogItem({
      id: 'text-analysis-default',
      label: '默认分析模型',
      model: String(form.llm_model_analysis || '').trim(),
      provider: form.llm_provider,
      description: '用于提案生成、分镜生成与提示词优化。',
      capability: '分析优化',
      enabled: true,
      is_default: false,
      operations: ['prompt_assist', 'workflow_proposal_generate', 'workflow_storyboard_generate'],
    }),
    createCatalogItem({
      id: 'text-review-default',
      label: '默认审核模型',
      model: String(form.llm_model_review || '').trim(),
      provider: form.llm_provider,
      description: '用于审核校对类文本任务。',
      capability: '审核校对',
      enabled: true,
      is_default: false,
      operations: [],
    }),
  ].filter((item) => item.model),
  image_models: [
    createCatalogItem({
      id: 'image-default',
      label: '默认图片模型',
      model: String(form.image_gen_model || '').trim(),
      provider: form.image_gen_provider,
      description: '沿用当前图片生成主配置的默认模型。',
      capability: '图片生成',
      enabled: true,
      is_default: true,
    }),
    ...knownImageCatalogSeeds.value,
  ].filter((item) => item.model),
  video_models: [
    createCatalogItem({
      id: 'video-default',
      label: '默认视频模型',
      model: String(form.video_gen_model || '').trim(),
      provider: form.video_gen_provider,
      description: '沿用当前视频生成主配置的默认模型。',
      capability: '视频生成',
      enabled: true,
      is_default: true,
    }),
    ...knownVideoCatalogSeeds.value,
  ].filter((item) => item.model),
  compliance_models: [
    createCatalogItem({
      id: 'compliance-default',
      label: '默认内容审核模型',
      model: String(form.compliance_model || '').trim(),
      provider: form.compliance_provider,
      description: '沿用当前内容审核主配置的默认模型。',
      capability: '内容审核',
      enabled: true,
      is_default: true,
    }),
  ].filter((item) => item.model && item.model !== 'default'),
}))
const suppressProviderSync = ref(false)
const llmProviderConfigCache = reactive<Record<string, LLMProviderConfig>>({})
const imageProviderConfigCache = reactive<Record<string, ProviderScopedMediaConfig>>({})
const ttsProviderConfigCache = reactive<Record<string, TTSProviderConfig>>({})
const videoProviderConfigCache = reactive<Record<string, ProviderScopedMediaConfig>>({})
const complianceProviderConfigCache = reactive<Record<string, ComplianceProviderConfig>>({})

function mergeRuntimeSeedCatalogs(catalogs: ModelCatalogConfig): ModelCatalogConfig {
  return {
    text_models: mergeCatalogFieldWithSeeds('text_models', catalogs.text_models || [], runtimeSeedCatalogs.value.text_models),
    image_models: mergeCatalogFieldWithSeeds('image_models', catalogs.image_models || [], runtimeSeedCatalogs.value.image_models),
    video_models: mergeCatalogFieldWithSeeds('video_models', catalogs.video_models || [], runtimeSeedCatalogs.value.video_models),
    compliance_models: mergeCatalogFieldWithSeeds('compliance_models', catalogs.compliance_models || [], runtimeSeedCatalogs.value.compliance_models),
  }
}

function replacePoolCache<T>(target: Record<string, T>, next: Record<string, T>) {
  Object.keys(target).forEach((key) => delete target[key])
  Object.entries(next).forEach(([key, value]) => {
    target[key] = value
  })
}

function getCurrentLLMProviderConfig(): LLMProviderConfig {
  return normalizeLLMProviderConfig(form.llm_provider, {
    api_base: String(form.llm_api_base || '').trim(),
    api_key: String(form.llm_api_key || '').trim(),
    model_chat: String(form.llm_model_chat || '').trim(),
    model_analysis: String(form.llm_model_analysis || '').trim(),
    model_review: String(form.llm_model_review || '').trim(),
  })
}

function getCurrentImageProviderConfig(): ProviderScopedMediaConfig {
  return normalizeImageProviderConfig(form.image_gen_provider, {
    api_base: String(form.image_gen_api_base || '').trim(),
    api_key: String(form.image_gen_api_key || '').trim(),
    api_secret: String(form.image_gen_api_secret || '').trim(),
    model: String(form.image_gen_model || '').trim(),
    priority_models: clonePriorityModels(form.image_gen_model_priority),
  }, { llmApiKey: String(form.llm_api_key || '').trim() })
}

function getCurrentTtsProviderConfig(): TTSProviderConfig {
  return normalizeTtsProviderConfig(form.tts_provider, {
    api_base: String(form.tts_api_base || '').trim(),
    api_key: String(form.tts_api_key || '').trim(),
    api_secret: String(form.tts_api_secret || '').trim(),
    app_key: String(form.tts_app_key || '').trim(),
    region: String(form.tts_region || '').trim(),
    voice_id: String(form.tts_voice_id || '').trim(),
    model: String(form.tts_model || '').trim(),
  })
}

function getCurrentVideoProviderConfig(): ProviderScopedMediaConfig {
  return normalizeVideoProviderConfig(form.video_gen_provider, {
    api_base: String(form.video_gen_api_base || '').trim(),
    api_key: String(form.video_gen_api_key || '').trim(),
    api_secret: String(form.video_gen_api_secret || '').trim(),
    model: String(form.video_gen_model || '').trim(),
    priority_models: clonePriorityModels(form.video_gen_model_priority),
  }, { llmApiKey: String(form.llm_api_key || '').trim() })
}

function getCurrentComplianceProviderConfig(): ComplianceProviderConfig {
  return normalizeComplianceProviderConfig(form.compliance_provider, {
    api_base: String(form.compliance_api_base || '').trim(),
    api_key: String(form.compliance_api_key || '').trim(),
    secret: String(form.compliance_secret || '').trim(),
    model: String(form.compliance_model || '').trim(),
  })
}

function applyLLMProviderConfig(provider: string, config?: Partial<LLMProviderConfig>) {
  const normalized = normalizeLLMProviderConfig(provider, config)
  form.llm_api_base = String(normalized.api_base || '')
  form.llm_api_key = String(normalized.api_key || '')
  form.llm_model_chat = String(normalized.model_chat || '')
  form.llm_model_analysis = String(normalized.model_analysis || '')
  form.llm_model_review = String(normalized.model_review || '')
}

function applyImageProviderConfig(provider: string, config?: Partial<ProviderScopedMediaConfig>) {
  const normalized = normalizeImageProviderConfig(provider, config, { llmApiKey: String(form.llm_api_key || '').trim() })
  form.image_gen_api_base = normalized.api_base
  form.image_gen_api_key = normalized.api_key
  form.image_gen_api_secret = normalized.api_secret
  form.image_gen_model = normalized.model
  form.image_gen_model_priority = normalized.priority_models
}

function applyTtsProviderConfig(provider: string, config?: Partial<TTSProviderConfig>) {
  const normalized = normalizeTtsProviderConfig(provider, config)
  form.tts_api_base = String(normalized.api_base || '')
  form.tts_api_key = String(normalized.api_key || '')
  form.tts_api_secret = String(normalized.api_secret || '')
  form.tts_app_key = String(normalized.app_key || '')
  form.tts_region = String(normalized.region || '')
  form.tts_voice_id = String(normalized.voice_id || '')
  form.tts_model = String(normalized.model || '')
}

function applyVideoProviderConfig(provider: string, config?: Partial<ProviderScopedMediaConfig>) {
  const normalized = normalizeVideoProviderConfig(provider, config, { llmApiKey: String(form.llm_api_key || '').trim() })
  form.video_gen_api_base = normalized.api_base
  form.video_gen_api_key = normalized.api_key
  form.video_gen_api_secret = normalized.api_secret
  form.video_gen_model = normalized.model
  form.video_gen_model_priority = normalized.priority_models
}

function applyComplianceProviderConfig(provider: string, config?: Partial<ComplianceProviderConfig>) {
  const normalized = normalizeComplianceProviderConfig(provider, config)
  form.compliance_api_base = String(normalized.api_base || '')
  form.compliance_api_key = String(normalized.api_key || '')
  form.compliance_secret = String(normalized.secret || '')
  form.compliance_model = String(normalized.model || '')
}

function syncProviderCaches() {
  llmProviderConfigCache[form.llm_provider] = getCurrentLLMProviderConfig()
  imageProviderConfigCache[form.image_gen_provider] = getCurrentImageProviderConfig()
  ttsProviderConfigCache[form.tts_provider] = getCurrentTtsProviderConfig()
  videoProviderConfigCache[form.video_gen_provider] = getCurrentVideoProviderConfig()
  complianceProviderConfigCache[form.compliance_provider] = getCurrentComplianceProviderConfig()
}

const patchForm = (payload: APIKeyConfig) => {
  suppressProviderSync.value = true
  const normalizedPayload = {
    ...payload,
    llm_backend: payload.llm_provider === 'dify' && payload.llm_backend === 'openclaw' ? 'dify' : payload.llm_backend,
    llm_provider: payload.llm_provider === 'dify' ? 'ali_tongyi' : payload.llm_provider,
    image_gen_provider: payload.image_gen_provider === 'kelin' ? 'kling' : payload.image_gen_provider,
    video_gen_provider: payload.video_gen_provider === 'kelin' ? 'kling' : payload.video_gen_provider,
    llm_provider_pool: payload.llm_provider_pool || {},
    image_provider_pool: payload.image_provider_pool || {},
    tts_provider_pool: payload.tts_provider_pool || {},
    video_provider_pool: payload.video_provider_pool || {},
    compliance_provider_pool: payload.compliance_provider_pool || {},
  }

  if (normalizedPayload.llm_provider === 'ali_tongyi') {
    normalizedPayload.llm_model_chat = sanitizeTongyiModel(normalizedPayload.llm_model_chat || '', 'chat')
    normalizedPayload.llm_model_analysis = sanitizeTongyiModel(normalizedPayload.llm_model_analysis || '', 'analysis')
    normalizedPayload.llm_model_review = sanitizeTongyiModel(normalizedPayload.llm_model_review || '', 'review')
  }

  normalizedPayload.image_gen_model_priority = Array.isArray(payload.image_gen_model_priority)
    ? payload.image_gen_model_priority
    : []
  normalizedPayload.video_gen_model_priority = Array.isArray(payload.video_gen_model_priority)
    ? payload.video_gen_model_priority
    : []

  if (normalizedPayload.image_gen_provider === 'ali_tongyi' && normalizedPayload.image_gen_model_priority.length) {
    normalizedPayload.image_gen_model = normalizedPayload.image_gen_model_priority[0]
  }
  if (normalizedPayload.image_gen_provider === 'fal' && (!normalizedPayload.image_gen_model || normalizedPayload.image_gen_model === 'default')) {
    normalizedPayload.image_gen_model = FAL_DEFAULT_IMAGE_MODEL
  }
  if (normalizedPayload.image_gen_provider === 'volcengine' && (!normalizedPayload.image_gen_model || normalizedPayload.image_gen_model === 'default')) {
    normalizedPayload.image_gen_model = VOLCENGINE_DEFAULT_IMAGE_MODEL
  }

  if (normalizedPayload.video_gen_provider === 'ali_tongyi' && normalizedPayload.video_gen_model_priority.length) {
    normalizedPayload.video_gen_model = normalizedPayload.video_gen_model_priority[0]
  }
  if (normalizedPayload.video_gen_provider === 'volcengine' && (!normalizedPayload.video_gen_model || normalizedPayload.video_gen_model === 'default')) {
    normalizedPayload.video_gen_model = VOLCENGINE_DEFAULT_VIDEO_MODEL
  }
  if (normalizedPayload.llm_provider === 'azure') {
    normalizedPayload.llm_model_chat = normalizedPayload.llm_model_chat || azureDefaultModels.chat
    normalizedPayload.llm_model_analysis = normalizedPayload.llm_model_analysis || azureDefaultModels.analysis
    normalizedPayload.llm_model_review = normalizedPayload.llm_model_review || azureDefaultModels.review
  }

  const {
    llm_provider_pool,
    image_provider_pool,
    tts_provider_pool,
    video_provider_pool,
    compliance_provider_pool,
    ...flatPayload
  } = normalizedPayload

  Object.assign(form, {
    ...form,
    ...Object.fromEntries(Object.entries(flatPayload).map(([key, value]) => [key, value ?? ''])),
  })

  replacePoolCache(
    llmProviderConfigCache,
    Object.fromEntries(
      Object.entries(llm_provider_pool).map(([provider, config]) => [provider, normalizeLLMProviderConfig(provider, config)]),
    ),
  )
  replacePoolCache(
    imageProviderConfigCache,
    Object.fromEntries(
      Object.entries(image_provider_pool).map(([provider, config]) => [provider, normalizeImageProviderConfig(provider, config, { llmApiKey: String(form.llm_api_key || '').trim() })]),
    ),
  )
  replacePoolCache(
    ttsProviderConfigCache,
    Object.fromEntries(
      Object.entries(tts_provider_pool).map(([provider, config]) => [provider, normalizeTtsProviderConfig(provider, config)]),
    ),
  )
  replacePoolCache(
    videoProviderConfigCache,
    Object.fromEntries(
      Object.entries(video_provider_pool).map(([provider, config]) => [provider, normalizeVideoProviderConfig(provider, config, { llmApiKey: String(form.llm_api_key || '').trim() })]),
    ),
  )
  replacePoolCache(
    complianceProviderConfigCache,
    Object.fromEntries(
      Object.entries(compliance_provider_pool).map(([provider, config]) => [provider, normalizeComplianceProviderConfig(provider, config)]),
    ),
  )
  syncProviderCaches()
  suppressProviderSync.value = false
}

const loadIntegrationStatus = async () => {
  integrationStatus.value = await configApi.getIntegrationStatus()
}

const loadConfig = async () => {
  loading.value = true
  try {
    const [data, status, catalogs] = await Promise.all([
      configApi.getAPIKeys(),
      configApi.getIntegrationStatus(),
      configApi.getModelCatalogs(),
    ])
    patchForm(data)
    integrationStatus.value = status
    modelCatalogs.value = mergeRuntimeSeedCatalogs(catalogs)
  } catch (error) {
    ElMessage.error('加载模型配置失败')
  } finally {
    loading.value = false
  }
}

const buildPayload = (): APIKeyUpdate => {
  if (form.llm_provider === 'ali_tongyi') {
    form.llm_model_chat = sanitizeTongyiModel(form.llm_model_chat, 'chat')
    form.llm_model_analysis = sanitizeTongyiModel(form.llm_model_analysis, 'analysis')
    form.llm_model_review = sanitizeTongyiModel(form.llm_model_review, 'review')
  }

  if (form.image_gen_provider === 'ali_tongyi') {
    if (!form.image_gen_model.trim() || form.image_gen_model === 'default') {
      form.image_gen_model = TONGYI_DEFAULT_IMAGE_MODEL
    }
    if (form.image_gen_model_priority.length) {
      form.image_gen_model = form.image_gen_model_priority[0]
    }
    if (!form.image_gen_api_base.trim()) {
      form.image_gen_api_base = resolveDashscopeImageApiBase(form.image_gen_model)
    }
    if (!form.image_gen_api_key.trim()) {
      form.image_gen_api_key = form.llm_api_key
    }
  }
  if (form.image_gen_provider === 'volcengine') {
    if (!form.image_gen_model.trim() || form.image_gen_model === 'default') {
      form.image_gen_model = VOLCENGINE_DEFAULT_IMAGE_MODEL
    }
    if (!form.image_gen_api_base.trim()) {
      form.image_gen_api_base = VOLCENGINE_ARK_IMAGE_API_BASE
    }
    if (!form.image_gen_api_key.trim()) {
      form.image_gen_api_key = form.llm_api_key
    }
  }
  if (form.image_gen_provider === 'fal') {
    if (!form.image_gen_model.trim() || form.image_gen_model === 'default') {
      form.image_gen_model = FAL_DEFAULT_IMAGE_MODEL
    }
  }

  if (form.llm_provider === 'azure') {
    if (!form.llm_model_chat.trim() || form.llm_model_chat === 'default') {
      form.llm_model_chat = azureDefaultModels.chat
    }
    if (!form.llm_model_analysis.trim() || form.llm_model_analysis === 'default') {
      form.llm_model_analysis = azureDefaultModels.analysis
    }
    if (!form.llm_model_review.trim() || form.llm_model_review === 'default') {
      form.llm_model_review = azureDefaultModels.review
    }
  }

  if (form.video_gen_provider === 'ali_tongyi') {
    if (!form.video_gen_model.trim() || form.video_gen_model === 'default') {
      form.video_gen_model = TONGYI_DEFAULT_VIDEO_MODEL
    }
    if (form.video_gen_model_priority.length) {
      form.video_gen_model = form.video_gen_model_priority[0]
    }
    if (!form.video_gen_api_base.trim()) {
      form.video_gen_api_base = DASHSCOPE_VIDEO_API_BASE
    }
    if (!form.video_gen_api_key.trim()) {
      form.video_gen_api_key = form.llm_api_key
    }
  }
  if (form.video_gen_provider === 'volcengine') {
    if (!form.video_gen_model.trim() || form.video_gen_model === 'default') {
      form.video_gen_model = VOLCENGINE_DEFAULT_VIDEO_MODEL
    }
    if (!form.video_gen_api_base.trim()) {
      form.video_gen_api_base = VOLCENGINE_ARK_VIDEO_API_BASE
    }
    if (!form.video_gen_api_key.trim()) {
      form.video_gen_api_key = form.llm_api_key
    }
  }

  syncProviderCaches()

  return {
    ...form,
    llm_provider_pool: Object.fromEntries(
      Object.entries(llmProviderConfigCache).map(([provider, config]) => [provider, normalizeLLMProviderConfig(provider, config)]),
    ),
    image_provider_pool: Object.fromEntries(
      Object.entries(imageProviderConfigCache).map(([provider, config]) => [provider, normalizeImageProviderConfig(provider, config, { llmApiKey: String(form.llm_api_key || '').trim() })]),
    ),
    tts_provider_pool: Object.fromEntries(
      Object.entries(ttsProviderConfigCache).map(([provider, config]) => [provider, normalizeTtsProviderConfig(provider, config)]),
    ),
    video_provider_pool: Object.fromEntries(
      Object.entries(videoProviderConfigCache).map(([provider, config]) => [provider, normalizeVideoProviderConfig(provider, config, { llmApiKey: String(form.llm_api_key || '').trim() })]),
    ),
    compliance_provider_pool: Object.fromEntries(
      Object.entries(complianceProviderConfigCache).map(([provider, config]) => [provider, normalizeComplianceProviderConfig(provider, config)]),
    ),
  }
}

watch(
  () => form.llm_provider,
  (newProvider, oldProvider) => {
    if (suppressProviderSync.value || !newProvider || newProvider === oldProvider) {
      return
    }

    if (oldProvider) {
      llmProviderConfigCache[oldProvider] = getCurrentLLMProviderConfig()
    }

    applyLLMProviderConfig(newProvider, llmProviderConfigCache[newProvider])
    llmProviderConfigCache[newProvider] = getCurrentLLMProviderConfig()
  }
)

watch(
  () => form.image_gen_provider,
  (newProvider, oldProvider) => {
    if (suppressProviderSync.value || !newProvider || newProvider === oldProvider) {
      return
    }

    if (oldProvider) {
      imageProviderConfigCache[oldProvider] = getCurrentImageProviderConfig()
    }

    applyImageProviderConfig(newProvider, imageProviderConfigCache[newProvider])
    imageProviderConfigCache[newProvider] = getCurrentImageProviderConfig()
  }
)

watch(
  () => form.tts_provider,
  (newProvider, oldProvider) => {
    if (suppressProviderSync.value || !newProvider || newProvider === oldProvider) {
      return
    }

    if (oldProvider) {
      ttsProviderConfigCache[oldProvider] = getCurrentTtsProviderConfig()
    }

    applyTtsProviderConfig(newProvider, ttsProviderConfigCache[newProvider])
    ttsProviderConfigCache[newProvider] = getCurrentTtsProviderConfig()
  }
)

watch(
  () => form.video_gen_provider,
  (newProvider, oldProvider) => {
    if (suppressProviderSync.value || !newProvider || newProvider === oldProvider) {
      return
    }

    if (oldProvider) {
      videoProviderConfigCache[oldProvider] = getCurrentVideoProviderConfig()
    }

    applyVideoProviderConfig(newProvider, videoProviderConfigCache[newProvider])
    videoProviderConfigCache[newProvider] = getCurrentVideoProviderConfig()
  }
)

watch(
  () => form.compliance_provider,
  (newProvider, oldProvider) => {
    if (suppressProviderSync.value || !newProvider || newProvider === oldProvider) {
      return
    }

    if (oldProvider) {
      complianceProviderConfigCache[oldProvider] = getCurrentComplianceProviderConfig()
    }

    applyComplianceProviderConfig(newProvider, complianceProviderConfigCache[newProvider])
    complianceProviderConfigCache[newProvider] = getCurrentComplianceProviderConfig()
  }
)

const handleSave = async () => {
  saving.value = true
  try {
    const apiPayload = buildPayload()
    const nextCatalogs = mergeRuntimeSeedCatalogs(modelCatalogs.value)
    const [response, catalogResponse] = await Promise.all([
      configApi.updateAPIKeys(apiPayload),
      configApi.updateModelCatalogs(nextCatalogs),
    ])
    if (response.data) {
      patchForm(response.data)
    }
    if (catalogResponse.data) {
      modelCatalogs.value = mergeRuntimeSeedCatalogs(catalogResponse.data)
    } else {
      modelCatalogs.value = nextCatalogs
    }
    await loadIntegrationStatus()
    ElMessage.success('模型与密钥配置已保存，并已应用到当前运行时')
  } catch (error) {
    ElMessage.error('保存配置失败')
  } finally {
    saving.value = false
  }
}

const integrationTagType = (item: IntegrationStatusItem) => {
  if (item.ready) return 'success'
  if (item.configured && !item.implemented) return 'warning'
  return 'info'
}

const integrationTagLabel = (item: IntegrationStatusItem) => {
  if (item.ready) return '已可用'
  if (item.configured && !item.implemented) return '待补接'
  if (item.configured) return '待验证'
  return '未配置'
}

onMounted(() => {
  if (!userStore.canAccessAdmin) {
    ElMessage.error('仅管理员可访问模型配置中心')
    router.replace('/dashboard')
    return
  }

  loadConfig()
})
</script>

<style scoped>
.page-shell {
  min-height: 100vh;
  padding: 32px;
  background: #f5f7fb;
}

.page-panel {
  max-width: 1320px;
  margin: 0 auto;
  padding: 32px;
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.08);
}

.page-header {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
}

.eyebrow {
  margin: 0 0 8px;
  color: #4f46e5;
  font-size: 13px;
  font-weight: 600;
}

.page-header h1 {
  margin: 0;
  font-size: 32px;
}

.page-subtitle {
  margin: 12px 0 0;
  color: #64748b;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.status-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
  border-radius: 18px;
  background: #f8fafc;
}

.status-card strong {
  font-size: 24px;
  color: #0f172a;
}

.status-card--soft {
  background: #fff7ed;
}

.status-card--accent {
  background: #eef2ff;
}

.status-board {
  margin-bottom: 20px;
}

.settings-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.card-block {
  padding: 24px;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  background: #fff;
}

.card-block--subtle {
  background: linear-gradient(180deg, #fffbeb 0%, #fff 100%);
  border-color: #fde68a;
}

.block-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.block-header h2,
.nested-panel h3 {
  margin: 0;
}

.block-subtitle {
  margin: 8px 0 0;
  color: #64748b;
  font-size: 14px;
}

.section-stack {
  display: grid;
  gap: 20px;
}

.section-title {
  margin: 0;
  font-size: 18px;
  color: #0f172a;
}

.section-note {
  margin: 8px 0 0;
  color: #64748b;
  font-size: 14px;
}

.section-divider {
  height: 1px;
  background: linear-gradient(90deg, rgba(226, 232, 240, 0), rgba(226, 232, 240, 1), rgba(226, 232, 240, 0));
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.integration-grid,
.quad-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.queue-board {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 18px;
}

.queue-card {
  padding: 18px;
  border-radius: 16px;
  background: linear-gradient(180deg, #f8fafc 0%, #eff6ff 100%);
  border: 1px solid #dbeafe;
}

.queue-card--video {
  background: linear-gradient(180deg, #f8fafc 0%, #ecfeff 100%);
  border-color: #bae6fd;
}

.queue-card__header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 14px;
}

.queue-card__header h3 {
  margin: 0;
  font-size: 16px;
}

.queue-card__header span,
.queue-card__empty {
  color: #64748b;
  font-size: 13px;
}

.queue-list {
  display: grid;
  gap: 10px;
}

.queue-list__item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.72);
}

.queue-list__item strong {
  min-width: 30px;
  color: #1d4ed8;
}

.queue-list__item span,
.queue-list__item small {
  display: block;
}

.queue-list__item small,
.media-config-note {
  color: #64748b;
  font-size: 12px;
  line-height: 1.6;
}

.media-config-note {
  margin-top: 8px;
}

.quad-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.integration-card {
  padding: 18px;
  border-radius: 16px;
  background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
  border: 1px solid #dbeafe;
}

.integration-card__head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.integration-card__head h3 {
  margin: 0;
  font-size: 18px;
}

.integration-card__head p {
  margin: 6px 0 0;
  color: #475569;
}

.integration-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
  color: #334155;
  font-size: 13px;
}

.integration-detail {
  margin: 14px 0 0;
  color: #475569;
  line-height: 1.6;
}

.nested-panel {
  padding: 18px;
  border-radius: 16px;
  background: #f8fafc;
}

.llm-guardrail-note {
  margin: 4px 0 18px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 13px;
  line-height: 1.6;
}

@media (max-width: 1080px) {
  .status-grid,
  .integration-grid,
  .queue-board,
  .quad-grid,
  .form-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .page-shell {
    padding: 16px;
  }

  .page-panel {
    padding: 24px;
  }

  .page-header,
  .header-actions {
    display: grid;
    grid-template-columns: 1fr;
  }
}
</style>